import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Select from "react-select";
import apiClient from "../api/apiClient";

const asText = (v) => (v === null || v === undefined ? "" : String(v));

const formatDate = (iso) => {
  if (!iso) return "";
  // ISO tipo "2024-09-13T00:00:00Z" -> DD/MM/AAAA
  const s = String(iso);
  const yyyy = s.slice(0, 4);
  const mm = s.slice(5, 7);
  const dd = s.slice(8, 10);
  if (yyyy && mm && dd) return `${dd}/${mm}/${yyyy}`;

  // Fallback: intentamos parsear con Date
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const dd2 = String(d.getDate()).padStart(2, "0");
  const mm2 = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy2 = String(d.getFullYear());
  return `${dd2}/${mm2}/${yyyy2}`;
};

const currencyToIntl = (moneda) => {
  const m = String(moneda || "").trim().toUpperCase();
  if (m === "USD" || m === "US$" || m === "$") return "USD";
  if (m === "GS" || m === "PYG" || m === "₲" || m === "G" || m === "GS.") return "PYG";
  return null;
};

const formatMoney = (value, moneda) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);

  const currency = currencyToIntl(moneda);
  if (!currency) return `${num.toLocaleString("es-PY")} ${moneda || ""}`.trim();

  return new Intl.NumberFormat("es-PY", {
    style: "currency",
    currency,
    maximumFractionDigits: currency === "USD" ? 2 : 0,
  }).format(num);
};

const Field = ({ label, value, tooltip, colClassName = "col-md-3" }) => (
  <div className={colClassName}>
    <label className="form-label">{label}</label>
    <input
      className="form-control"
      value={value}
      readOnly
      data-bs-toggle={tooltip ? "tooltip" : undefined}
      data-bs-placement="top"
      data-bs-html={tooltip ? "true" : undefined}
      data-bs-title={tooltip || undefined}
    />
  </div>
);

const GestionInmobiliariaCotizacionDetalle = () => {
  const { docEntry } = useParams();
  const [data, setData] = useState(null);
  const [unidadInfo, setUnidadInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creatingContrato, setCreatingContrato] = useState(false);
  const [contratoMsg, setContratoMsg] = useState("");

  // Distribución para facturación (REM_CONT_DACollection) al crear el contrato
  const [showDistribModal, setShowDistribModal] = useState(false);
  const [distribRows, setDistribRows] = useState([]);

  const [clientesOptions, setClientesOptions] = useState([]);
  const [clientesLoading, setClientesLoading] = useState(false);
  const [clientesError, setClientesError] = useState("");
  const [selectedCliente, setSelectedCliente] = useState("");

  const [ctasOptions, setCtasOptions] = useState([]);
  const [ctasLoading, setCtasLoading] = useState(false);
  const [ctasError, setCtasError] = useState("");
  const [selectedCtaFact, setSelectedCtaFact] = useState("");

  const clienteSelectOptions = useMemo(() => {
    return (clientesOptions || [])
      .filter((c) => c?.Code)
      .map((c) => ({ value: String(c.Code), label: `${c.Code} - ${c.Name || ""}`.trim() }));
  }, [clientesOptions]);

  const selectedClienteOption = useMemo(() => {
    if (!selectedCliente) return null;
    return (
      clienteSelectOptions.find((o) => o.value === String(selectedCliente)) || {
        value: String(selectedCliente),
        label: String(selectedCliente),
      }
    );
  }, [selectedCliente, clienteSelectOptions]);

  const ctaFactSelectOptions = useMemo(() => {
    return (ctasOptions || [])
      .filter((c) => c?.Code)
      .map((c) => ({ value: String(c.Code), label: `${c.Code} - ${c.Name || ""}`.trim() }));
  }, [ctasOptions]);

  const selectedCtaFactOption = useMemo(() => {
    if (!selectedCtaFact) return null;
    return (
      ctaFactSelectOptions.find((o) => o.value === String(selectedCtaFact)) || {
        value: String(selectedCtaFact),
        label: String(selectedCtaFact),
      }
    );
  }, [selectedCtaFact, ctaFactSelectOptions]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      setError("");
      setUnidadInfo(null);
      setContratoMsg("");
      try {
        const user = JSON.parse(localStorage.getItem("sapUser") || "{}");
        const companyDb = user?.CompanyDB;
        const res = await apiClient.get(`/inmobiliaria/cotizaciones/${docEntry}`, {
          params: { CompanyDB: companyDb },
        });
        if (!alive) return;
        setData(res.data);

        const unidadDocEntry = res.data?.U_COD_LOTE;
        if (unidadDocEntry !== null && unidadDocEntry !== undefined && String(unidadDocEntry).trim() !== "") {
          try {
            const uniRes = await apiClient.get(`/inmobiliaria/unidades/${unidadDocEntry}`);
            if (!alive) return;
            setUnidadInfo(uniRes.data);
          } catch {
            // No bloqueamos la pantalla si no se puede resolver la unidad
            if (alive) setUnidadInfo(null);
          }
        }
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.error || e?.message || "Error cargando cotización");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [docEntry]);

  useEffect(() => {
    let alive = true;
    const loadClientes = async () => {
      setClientesLoading(true);
      setClientesError("");
      try {
        const user = JSON.parse(localStorage.getItem("sapUser") || "{}");
        const companyDb = user?.CompanyDB;
        const res = await apiClient.get("/inmobiliaria/clientes", {
          params: { CompanyDB: companyDb },
        });
        if (!alive) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setClientesOptions(list);
      } catch (e) {
        if (!alive) return;
        setClientesError(e?.response?.data?.error || e?.message || "Error cargando clientes");
        setClientesOptions([]);
      } finally {
        if (alive) setClientesLoading(false);
      }
    };
    loadClientes();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const loadCtas = async () => {
      setCtasLoading(true);
      setCtasError("");
      try {
        const user = JSON.parse(localStorage.getItem("sapUser") || "{}");
        const companyDb = user?.CompanyDB;
        const res = await apiClient.get("/inmobiliaria/cuentas-contables", {
          params: { CompanyDB: companyDb },
        });
        if (!alive) return;
        setCtasOptions(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!alive) return;
        setCtasError(e?.response?.data?.error || e?.message || "Error cargando cuentas contables");
        setCtasOptions([]);
      } finally {
        if (alive) setCtasLoading(false);
      }
    };
    loadCtas();
    return () => {
      alive = false;
    };
  }, []);

  const onPasarAContrato = async () => {
    setCreatingContrato(true);
    setContratoMsg("");
    setError("");
    try {
      const user = JSON.parse(localStorage.getItem("sapUser") || "{}");
      const companyDb = user?.CompanyDB;
      if (!companyDb) {
        setError("CompanyDB no está disponible. Cerrá sesión y volvé a ingresar seleccionando la base de datos.");
        return;
      }
      if (!selectedCliente) {
  setError("Seleccioná un cliente antes de pasar a contrato");
        return;
      }
      const res = await apiClient.post(
        "/inmobiliaria/contratos",
        {
          cotizacionDocEntry: Number(docEntry),
          header: {
            U_CODSR: selectedCliente,
            U_PORC_CISR: "100",
            U_CTA_FACT: selectedCtaFact || null,
          },
          distribucion: Array.isArray(distribRows)
            ? distribRows.map((r) => ({
                U_CARDCODE: r?.U_CARDCODE ? String(r.U_CARDCODE) : "",
                U_FACTURAR: r?.U_FACTURAR === "S" ? "S" : "N",
                U_PORCENTAJE: r?.U_PORCENTAJE === null || r?.U_PORCENTAJE === undefined ? "" : String(r.U_PORCENTAJE),
              }))
            : [],
        },
        {
          params: { CompanyDB: companyDb },
        }
      );
      const createdDocEntry = res?.data?.DocEntry;
      if (createdDocEntry !== null && createdDocEntry !== undefined && String(createdDocEntry).trim() !== "") {
        setContratoMsg(`Contrato creado (DocEntry: ${createdDocEntry}).`);
      } else {
        setContratoMsg("Contrato creado.");
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Error creando contrato");
    } finally {
      setCreatingContrato(false);
    }
  };

  const distribClienteOptions = useMemo(() => {
    return (clientesOptions || [])
      .filter((c) => c?.Code)
      .map((c) => ({ value: String(c.Code), label: `${c.Code} - ${c.Name || ""}`.trim() }));
  }, [clientesOptions]);

  const openDistribModal = () => {
    // Precarga: si no hay filas todavía, ponemos el titular seleccionado con 100% y facturar = S
    if (!Array.isArray(distribRows) || distribRows.length === 0) {
      if (selectedCliente) {
        setDistribRows([
          {
            U_CARDCODE: String(selectedCliente),
            U_FACTURAR: "S",
            U_PORCENTAJE: "100",
          },
        ]);
      } else {
        setDistribRows([{ U_CARDCODE: "", U_FACTURAR: "S", U_PORCENTAJE: "0" }]);
      }
    }
    setShowDistribModal(true);
  };

  const closeDistribModal = () => {
    setShowDistribModal(false);
  };

  // Modal sin dependencia de Bootstrap JS: controlado 100% por React.
  useEffect(() => {
    if (!showDistribModal) return;

    // Evita scroll del fondo mientras el modal está abierto.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [showDistribModal]);

  const addDistribRow = () => {
    setDistribRows((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      { U_CARDCODE: "", U_FACTURAR: "S", U_PORCENTAJE: "0" },
    ]);
  };

  const removeDistribRow = (idx) => {
    setDistribRows((prev) => (Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : []));
  };

  const updateDistribRow = (idx, patch) => {
    setDistribRows((prev) =>
      (Array.isArray(prev) ? prev : []).map((r, i) => (i === idx ? { ...r, ...patch } : r))
    );
  };

  // Inicializa tooltips de Bootstrap en los inputs que lo tengan.
  useEffect(() => {
    // bootstrap.bundle ya está cargado globalmente en index.js
    const bootstrap = window?.bootstrap;
    if (!bootstrap?.Tooltip) return;

    const els = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    const instances = els.map(
      (el) =>
        new bootstrap.Tooltip(el, {
          trigger: "hover focus",
          container: "body",
          html: true,
          // Permitimos solo <br> para mostrar líneas debajo del otro
          sanitize: true,
          allowList: {
            ...bootstrap.Tooltip.Default.allowList,
            br: [],
          },
        })
    );
    return () => {
      instances.forEach((i) => {
        try {
          i.dispose();
        } catch {
          // noop
        }
      });
    };
  }, [unidadInfo, data]);

  const cuotas = useMemo(() => {
    const list = data?.REM_COT_DCollection;
    return Array.isArray(list) ? list : [];
  }, [data]);

  const moneda = data?.U_MONEDA;

  const unidadDisplay = useMemo(() => {
    const raw = asText(data?.U_COD_LOTE);

    const lines = unidadInfo?.Lines;
    if (Array.isArray(lines) && lines.length > 0) {
      const first = lines[0];
      const itemCode = first?.ItemCode ? String(first.ItemCode) : "";
      const itemName = first?.ItemName ? String(first.ItemName) : "";
      const value = [itemCode, itemName].filter(Boolean).join(" - ") || raw;

      const tooltipText =
        lines.length <= 1
          ? ""
          : lines
              .slice(1)
              .map((l) => {
                const ic = l?.ItemCode ? String(l.ItemCode) : "";
                const iname = l?.ItemName ? String(l.ItemName) : "";
                return [ic, iname].filter(Boolean).join(" - ");
              })
              .filter(Boolean)
              .join("\n");

      // Bootstrap tooltip con html=true: convertimos \n en <br />
      const tooltip = tooltipText ? tooltipText.replace(/\n/g, "<br />") : "";

      return { value, tooltip };
    }

    // fallback al comportamiento anterior
    const itemName = unidadInfo?.ItemName;
    const itemCode = unidadInfo?.ItemCode;
    if (itemName && itemCode) return { value: `${itemCode} - ${itemName}`, tooltip: "" };
    if (itemName) return { value: String(itemName), tooltip: "" };
    if (itemCode) return { value: `${itemCode}${raw ? ` (Unidad: ${raw})` : ""}`, tooltip: "" };
    return { value: raw, tooltip: "" };
  }, [data, unidadInfo]);

  return (
    <div className="container mt-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="mb-0">Cotización Nro {docEntry}</h2>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onPasarAContrato}
            disabled={creatingContrato || loading}
            title="Crea un contrato (REM_CONT) copiando datos de esta cotización"
          >
            {creatingContrato ? "Creando contrato..." : "Pasar a Contrato"}
          </button>
          <Link className="btn btn-outline-secondary" to="/inmobiliaria/cotizaciones">
            Volver
          </Link>
        </div>
      </div>

      {showDistribModal && (
        <>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            style={{ display: "block" }}
            onMouseDown={(e) => {
              // Click en backdrop cierra, click dentro no.
              if (e.target === e.currentTarget) closeDistribModal();
            }}
          >
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Distribución de facturas</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeDistribModal} />
                </div>
                <div className="modal-body">
                  <div className="alert alert-info py-2">
                    Podés cargar varios socios (CardCode) con su porcentaje. Las filas con porcentaje 0 o con
                    “Facturar = No” se guardan, pero no deberían generar facturas.
                  </div>

                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th style={{ minWidth: 320 }}>Socio</th>
                          <th style={{ width: 140 }}>Facturar</th>
                          <th style={{ width: 140 }}>%</th>
                          <th style={{ width: 80 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {(Array.isArray(distribRows) ? distribRows : []).map((row, idx) => {
                          const selectedOpt = row?.U_CARDCODE
                            ? distribClienteOptions.find((o) => o.value === String(row.U_CARDCODE)) || {
                                value: String(row.U_CARDCODE),
                                label: String(row.U_CARDCODE),
                              }
                            : null;

                          return (
                            <tr key={idx}>
                              <td>
                                <Select
                                  classNamePrefix="react-select"
                                  isClearable
                                  isLoading={clientesLoading}
                                  options={distribClienteOptions}
                                  value={selectedOpt}
                                  onChange={(opt) => updateDistribRow(idx, { U_CARDCODE: opt?.value || "" })}
                                  placeholder="Buscar socio..."
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    menu: (base) => ({ ...base, zIndex: 9999 }),
                                  }}
                                />
                              </td>
                              <td>
                                <div className="form-check form-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={(row?.U_FACTURAR || "N") === "S"}
                                    onChange={(e) =>
                                      updateDistribRow(idx, { U_FACTURAR: e.target.checked ? "S" : "N" })
                                    }
                                    id={`dist_fact_${idx}`}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`dist_fact_${idx}`}
                                    style={{ userSelect: "none" }}
                                  >
                                    {(row?.U_FACTURAR || "N") === "S" ? "Sí" : "No"}
                                  </label>
                                </div>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control form-control-sm"
                                  value={row?.U_PORCENTAJE ?? ""}
                                  onChange={(e) => updateDistribRow(idx, { U_PORCENTAJE: e.target.value })}
                                />
                              </td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => removeDistribRow(idx)}
                                  disabled={(Array.isArray(distribRows) ? distribRows.length : 0) <= 1}
                                  title="Eliminar fila"
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addDistribRow}>
                    + Agregar socio
                  </button>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={closeDistribModal}>
                    Cerrar
                  </button>
                  <button type="button" className="btn btn-primary" onClick={closeDistribModal}>
                    Guardar
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {loading && <div className="alert alert-info">Cargando...</div>}
      {!loading && error && <div className="alert alert-danger">{error}</div>}
  {!loading && !error && contratoMsg && <div className="alert alert-success">{contratoMsg}</div>}

  {!loading && !error && clientesError && <div className="alert alert-warning">{clientesError}</div>}
  {!loading && !error && ctasError && <div className="alert alert-warning">{ctasError}</div>}

      {!loading && !error && data && (
        <div className="card shadow-sm">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-5">
                <label className="form-label">Cliente</label>
                <Select
                  classNamePrefix="react-select"
                  isClearable
                  isLoading={clientesLoading}
                  options={clienteSelectOptions}
                  value={selectedClienteOption}
                  onChange={(opt) => setSelectedCliente(opt?.value || "")}
                  placeholder="Buscar cliente..."
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>

              <div className="col-12 col-md-2 d-flex align-items-end">
                <button
                  type="button"
                  className="btn btn-outline-primary w-100"
                  style={{ height: 38 }}
                  onClick={openDistribModal}
                  disabled={loading}
                  title="Configura cómo se dividirá la facturación entre distintos socios"
                >
                  Distribución
                </button>
              </div>

              <div className="col-12 col-md-5">
                <label className="form-label">Cuenta contable</label>
                <Select
                  classNamePrefix="react-select"
                  isClearable
                  isLoading={ctasLoading}
                  options={ctaFactSelectOptions}
                  value={selectedCtaFactOption}
                  onChange={(opt) => setSelectedCtaFact(opt?.value || "")}
                  placeholder="Buscar cuenta contable..."
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </div>

            <div className="row mt-2 g-3 align-items-end">
              <Field label="Estado" value={asText(data?.U_ESTADO)} colClassName="col-12 col-md" />
              <Field label="Moneda" value={asText(moneda)} colClassName="col-12 col-md" />
              <Field label="Tipo de Cambio" value={asText(data?.U_TC)} colClassName="col-12 col-md" />
              <Field
                label="Nro Contrato"
                value={asText(data?.U_NROCONTRATO)}
                colClassName="col-12 col-md"
              />
              <Field
                label="Fecha Cotización"
                value={formatDate(data?.U_FECHACO)}
                colClassName="col-12 col-md"
              />
              <Field
                label="Vencimiento"
                value={formatDate(data?.U_FECHAV)}
                colClassName="col-12 col-md"
              />
            </div>

            <div className="row mt-3 g-3 align-items-end">
              <Field label="Precio Venta" value={formatMoney(data?.U_PRECIOVE, moneda)} />
              <Field label="Entrega" value={formatMoney(data?.U_ADELA, moneda)} />
              <Field label="Monto Cuota" value={formatMoney(data?.U_MONTCUO, moneda)} />
              {unidadInfo?.U_PrecioUnitario !== null &&
                unidadInfo?.U_PrecioUnitario !== undefined && (
                  <Field
                    label="Precio Unitario"
                    value={formatMoney(unidadInfo?.U_PrecioUnitario, moneda)}
                  />
                )}
            </div>

            <div className="row mt-3 g-3 align-items-end">
              <Field
                label="Unidad"
                value={unidadDisplay.value}
                tooltip={unidadDisplay.tooltip}
                colClassName="col-12"
              />
            </div>

            <hr className="my-4" />

            <h5 className="mb-3">Cuotas</h5>
            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle">
                <thead className="table-primary">
                  <tr>
                    <th style={{ width: 140 }}>Nro Cuota</th>
                    <th style={{ width: 180 }}>Vencimiento</th>
                    <th className="text-end">Cuota</th>
                  </tr>
                </thead>
                <tbody>
                  {cuotas.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center text-muted py-4">
                        Sin detalle de cuotas
                      </td>
                    </tr>
                  )}
                  {cuotas.map((c, idx) => (
                    <tr key={`${c?.LineId ?? "line"}-${idx}`}>
                      <td>{asText(c?.U_NROCUO)}</td>
                      <td>{formatDate(c?.U_FECHAVTO)}</td>
                      <td className="text-end">{formatMoney(c?.U_CUOTA, moneda)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionInmobiliariaCotizacionDetalle;
